"use client"

import { useId } from "react"

import { cn } from "@/lib/utils"

interface LogoProps {
  className?: string
  gradientFrom?: string
  gradientTo?: string
  variant?: "default" | "gradient" | "white"
  size?: "sm" | "md" | "lg" | "xl"
}

const sizeClasses = {
  sm: "h-8 w-auto",
  md: "h-10 w-auto",
  lg: "h-12 w-auto",
  xl: "h-16 w-auto",
}

export function Logo({
  className = "",
  gradientFrom = "#e11d48", // Romance red by default
  gradientTo = "#db2777", // Romance pink by default
  variant = "gradient",
  size = "md",
}: LogoProps) {
  const id = useId()
  const gradientId = `logo-gradient-${id}`

  // Color variants
  const fillColor = variant === "white" ? "#ffffff" : variant === "default" ? "#c7c7c7" : `url(#${gradientId})`

  return (
    <svg
      id="authorythm-logo"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      viewBox="0 0 600 479.2"
      className={cn("block shrink-0", sizeClasses[size], className)}
    >
      <defs>
        {variant === "gradient" && (
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={gradientFrom} />
            <stop offset="100%" stopColor={gradientTo} />
          </linearGradient>
        )}
      </defs>

      {/* Main "A" Letter - the centerpiece that gets the gradient */}
      <path
        fill={fillColor}
        d="M315.2,38.5c2.1-.5,4.9-.8,7.3-.8,14.8.2,29.8.4,44.9.6,17.5.5,35.2-.6,52.8,1.3,83.2,9.9,119,119.2,55.6,175.3-9,7.9-19.6,14.2-31,18.7-3.9,1.7-10.6,4.1-10.1,7.5,17,24.8,63,86.9,70.9,98.3,1.5,2.4-2.2,1.6-5.5.7-34.4-12.6-71.7-18.7-106.6-10.7-32.5,7.2-64.1,25.7-83.8,53-1.6,2.4-6.2,7-7.2,6.3-.8-.2-.5-1.6-.2-2.3,6.4-14.9,45-105,51.4-120,.5-1.1,1.2-2.3,2.4-2.5,1-.3,4.8,1,7.1,2.4,6.7,3.5,19.6,10.4,27.4,14.5,1.4.7,8.1,4.6,7.2,3.2-7.4-11.3-53.8-79.6-62.6-92.8-.9-1.3-1.8-3.1-.6-4.4,1.4-2.1,8.2-2,11.8-1.5,15.9.9,34.5,1.2,50,.5,22.1-.5,40.8-11.5,46.6-33.4,4.8-17.8-1.7-38-17.5-48.5-6.3-4.3-15-7.2-22.7-8-28.7-2.3-57.8-.4-86.3-.5-3.9-.2-5.4-2.3-5.3-5.8,0-9.7,0-35.1,0-44.8,0-2.9.6-5.6,3.9-6.3h.1Z"
      />

      {/* Supporting decorative elements - subtle gray */}
      <path
        fill="#c7c7c7"
        d="M259.2,392.6c-44.7-37.9-112-33.3-163-10.5-4.1,1.5-12.4,7-14.6,2.1-1.2-3.3.9-10.5,2.5-14.7,2-5.2,5.8-8,10.7-10.1,63.7-28.8,143-22.4,188.7,33.8,4.1,4.8,7.7,10.5,10.9,15.7,1.7,2.3,2.8,4.4,5.3,4.5,1.6,0,3.1-1.8,4.7-4.2,39.3-65.2,120.9-80.7,189.2-54.1,3.8,1.4,8.3,3.2,11.9,4.8,5.4,2.3,8.3,4.9,10,10.8,1.2,4.1,2.9,8.6,2.6,12.9-1.1,5.9-10.2.5-13.9-.9-8-3.5-16-7-24.4-9.5-34.6-10.2-74.9-13.3-108.8.9-10.3,4.4-19.6,10.1-29,16.3-1.2.8-6.1,4.6-4.2,5.3.8.3,2.7-.2,4.9-1.1,57.4-26.6,127.8-15.1,182.7,14.2,2.8,1.6,6,3.5,8.9,5,2.1,1,3.3,1.9,5.2,1.8,1.3-.2.8-2.4-.2-6-1.5-5.2-3.5-13-5.1-18.9-1-4.7-4.3-12.7-2-13.1,1.9-.1,3.9,1.2,5.7,2.2,4.1,2.5,8.6,5,9.9,9.7,3.9,13.4,14.7,45.8,17.6,58.2.1,4.9-7,0-9.3-1.6-70.4-51.1-180.1-76.1-249-11.5-5.9,5.7-9.3,5.1-15.1-.2-59.6-56.5-160.8-44.6-224.4-3-8.1,5.1-16.1,10.6-24.3,15.9-3.2,1.8-10.6,7.3-8.5.5,3.4-11.7,12.5-45,16-57.1,1.5-5.4,7.4-9,12.2-11.6,5.3-2.5,5.2,1.6,4.2,5.9-.8,3.7-2.3,8.4-3.7,13-1.5,5-3,9.1-3.9,13.1-1.4,6.2,3.5,3.8,7,1.9,51.8-30,117.6-44.5,175.3-24,4.8,1.4,9.3,2.7,13.7,4.5,2.4.7,7.5,3.4,3.5-.8h0Z"
      />
      <path
        fill="#c7c7c7"
        d="M308.4,298.8c-1,.2-2.3-.8-2.7-1.9-7-15.4-65.3-143.5-71.9-158-.7-1.5-1.9-2-3.7-.2-1.3,1.3-2.8,3.7-3.8,5.7-9.9,20.3-19,41.3-28.2,62.5-4.7,10.7-9.5,21.3-14.5,31.7-2.7,5.5-5.4,11.3-9.8,15.3-5.7,5-12.9,7.8-19.4,11.6-9.7,5.1-19.3,11.1-28,18-8.9,6.9-17.4,14.8-24.9,23.2-1.3,1.4-2.8,2.8-4.1,3.8-1.1.8-1.9,1.3-2.3,1.3-.5,0-.6-.3-.5-.7,7.2-15.2,127.1-268.4,134.4-283.9.2-.4.7-.5,1.2-.3,1.9.4,3.3,2.6,4.1,4.6,17.1,34.9,53.2,109.4,70.7,145,1.7,3.5,3,6.1,3.7,7.6.7,1.5.9,1.6.9,3.1,0,13.3,0,93.3,0,109.2,0,1-.3,2.4-1.2,2.5h0Z"
      />
      <path
        fill="#c7c7c7"
        d="M297.3,388.4c-.4.5-1.6-.3-3.3-2.2-39.1-53.8-109-72.1-172.1-54.3-8,2-15.4,5.6-23.3,7.4-7.5,1.3-3.8-4.9-1.2-8.1,32.2-41.8,78.8-73.8,132.7-74.6,4.6,0,7.8.9,10.4,4.9,21.1,38,36.6,78.6,54.6,118,1,2.3,2.8,7.2,2.1,8.8h0Z"
      />
    </svg>
  )
}
